import HealthScoreQuestionnaire from "@/features/HealthScore/components/HealthScoreQuestionnaire";
import {
  Question,
  QuestionnaireAnswer,
  QuestionType,
  QuestionnaireType,
  MentalWellbeingFrequency,
  QuestionnaireAnswers,
} from "@/features/HealthScore/components/HealthScoreQuestionnaire/types";
import useGetHealthScoreQuestionnaire, {
  healthScoreQuestionnaireQueryKey,
} from "@/features/HealthScore/components/HealthScoreQuestionnaire/hooks/useGetHealthScoreQuestionnaire";
import {
  submitHealthScoreQuestionnaire,
  SubmitHealthScoreQuestionnairePayload,
} from "@/services/core-api-adapter";
import DefaultError from "@/features/Common/components/DefaultError";
import { useQueryClient } from "@tanstack/react-query";
import FullscreenLoadingIndicator from "@/features/Common/components/FullscreenLoadingIndicator";
import { Typography } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import useGetHealthProfile from "../../hooks/useGetHealthProfile";
import { healthScoreSummaryQueryKey } from "../../hooks/useGetHealthScoreSummary";
import { healthTrendsQueryKey } from "../../hooks/useHealthTrends";

export interface MentalWellbeingQuestionnaireContentProps {
  onClose?: () => void;
  onSubmitComplete?: () => void;
}

const FREQUENCY_VALUES = [
  MentalWellbeingFrequency.NOT_AT_ALL,
  MentalWellbeingFrequency.SEVERAL_DAYS,
  MentalWellbeingFrequency.MORE_THAN_HALF,
  MentalWellbeingFrequency.NEARLY_EVERYDAY,
];

const MENTAL_WELLBEING_QUESTIONS: Question[] = [
  {
    type: QuestionType.NERVOUS_OR_ON_EDGE,
    meta: {
      description: true,
      values: FREQUENCY_VALUES,
    },
  },
  {
    type: QuestionType.UNCONTROLLABLE_WORRY,
    meta: {
      description: true,
      values: FREQUENCY_VALUES,
    },
  },
  {
    type: QuestionType.LOW_INTEREST_OR_PLEASURE,
    meta: {
      description: true,
      values: FREQUENCY_VALUES,
    },
  },
  {
    type: QuestionType.FEELING_DOWN_OR_HOPELESS,
    meta: {
      description: true,
      values: FREQUENCY_VALUES,
    },
  },
];

export default function MentalWellbeingQuestionnaireContent({
  onClose,
  onSubmitComplete,
}: MentalWellbeingQuestionnaireContentProps = {}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { refetchHealthProfile: refetch } = useGetHealthProfile();
  const [submitError, setSubmitError] = useState(false);

  const {
    questionnaireData,
    isQuestionnaireLoading,
    questionnaireFetchError,
    refetchQuestionnaire,
  } = useGetHealthScoreQuestionnaire(QuestionnaireType.MENTAL_WELLBEING);

  const initialAnswers: QuestionnaireAnswers = {};
  MENTAL_WELLBEING_QUESTIONS.forEach((question) => {
    const initialAnswer = questionnaireData?.[question.type];
    if (initialAnswer) {
      initialAnswers[question.type] = initialAnswer;
    }
  });

  const handleSubmit = async (answers: QuestionnaireAnswer[]) => {
    const payload: SubmitHealthScoreQuestionnairePayload = {
      questionnaireType: QuestionnaireType.MENTAL_WELLBEING,
      value: answers,
    };
    try {
      await submitHealthScoreQuestionnaire(payload);
      await Promise.all([
        refetch(),
        queryClient.invalidateQueries({
          queryKey: healthScoreQuestionnaireQueryKey(
            QuestionnaireType.MENTAL_WELLBEING
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: healthScoreSummaryQueryKey,
        }),
        queryClient.invalidateQueries({
          queryKey: healthTrendsQueryKey("Phq2Score"),
        }),
        queryClient.invalidateQueries({
          queryKey: healthTrendsQueryKey("Gad2Score"),
        }),
      ]);
      if (onSubmitComplete) onSubmitComplete();
      else if (onClose) onClose();
    } catch {
      setSubmitError(true);
    }
  };

  const renderQuestionnaireError = (onTryAgain: () => void) => (
    <DefaultError onComeBackLater={onClose} onTryAgain={onTryAgain}>
      <Typography variant="h2" pb={2}>
        {t("common.genericErrorMessage")}
      </Typography>
    </DefaultError>
  );

  if (isQuestionnaireLoading) {
    return <FullscreenLoadingIndicator mainIconType="face" />;
  }

  if (questionnaireFetchError) {
    return renderQuestionnaireError(() => refetchQuestionnaire());
  }

  if (submitError) {
    return renderQuestionnaireError(() => setSubmitError(false));
  }

  return (
    <HealthScoreQuestionnaire
      questions={MENTAL_WELLBEING_QUESTIONS}
      translationPrefix="MentalWellbeing.questionnaire"
      onSubmit={handleSubmit}
      onClose={onClose}
      title={t("MentalWellbeing.questionnaire.title")}
      initialAnswers={initialAnswers}
    />
  );
}
